import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPublicSettings, type PublicSettingsResponse } from '@/api/settings';
import { getPublicTools, type ToolDto } from '@/api/tool';
import { getPublicSolutions, type SolutionDto } from '@/api/solution';

function readText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

export default function Footer() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<PublicSettingsResponse | null>(null);
  const [toolLinks, setToolLinks] = useState<Array<{ id: string; name: string }>>([]);
  const [solutionLinks, setSolutionLinks] = useState<Array<{ id: string; title: string }>>([]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        const [settingRes, toolRes, solutionRes] = await Promise.all([
          getPublicSettings(),
          getPublicTools(),
          getPublicSolutions(),
        ]);
        if (!alive) return;

        setSettings(settingRes);

        const tools = Array.isArray(toolRes?.list)
          ? toolRes.list
              .map((item: ToolDto) => ({ id: String(item.id ?? ''), name: readText(item.name) }))
              .filter((item) => item.id && item.name)
              .slice(0, 5)
          : [];
        setToolLinks(tools);

        const solutions = Array.isArray(solutionRes?.list)
          ? solutionRes.list
              .map((item: SolutionDto) => ({ id: String(item.id ?? ''), title: readText(item.title) }))
              .filter((item) => item.id && item.title)
              .slice(0, 5)
          : [];
        setSolutionLinks(solutions);
      } catch (err) {
        console.error('Failed to fetch public footer data:', err);
        if (!alive) return;
        setSettings(null);
        setToolLinks([]);
        setSolutionLinks([]);
      }
    };

    void load();

    const refresh = () => {
      void load();
    };

    window.addEventListener('wanxiang_settings_updated', refresh);
    window.addEventListener('wanxiang_tools_updated', refresh);
    window.addEventListener('wanxiang_solutions_updated', refresh);

    return () => {
      alive = false;
      window.removeEventListener('wanxiang_settings_updated', refresh);
      window.removeEventListener('wanxiang_tools_updated', refresh);
      window.removeEventListener('wanxiang_solutions_updated', refresh);
    };
  }, []);

  const siteName = readText(settings?.siteName) || '未配置站点';
  const logoPreview = readText(settings?.logoPreview);
  const footerDesc = readText(settings?.footerDesc) || readText(settings?.siteSlogan);
  const copyright = readText(settings?.footerCopyright);
  const icp = readText(settings?.icp);

  const socialLinks = useMemo(() => {
    const list = [
      { icon: 'ri-github-line', href: readText(settings?.socialGithub) },
      { icon: 'ri-wechat-line', href: readText(settings?.socialWechat) },
      { icon: 'ri-weibo-line', href: readText(settings?.socialWeibo) },
      { icon: 'ri-twitter-x-line', href: readText(settings?.socialTwitter) },
    ];

    return list.filter((item) => item.href);
  }, [settings]);

  return (
    <footer className="bg-[#0f172a] text-gray-400">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] rounded-lg overflow-hidden shrink-0">
                {logoPreview ? (
                  <img src={logoPreview} alt="logo" className="w-full h-full object-cover" />
                ) : (
                  <i className="ri-store-2-line text-white text-sm"></i>
                )}
              </div>
              <span className="font-bold text-white text-lg">{siteName}</span>
            </div>
            {footerDesc ? <p className="text-sm leading-relaxed">{footerDesc}</p> : <p className="text-sm text-gray-500">暂无站点说明</p>}
            {socialLinks.length > 0 && (
              <div className="flex gap-3 mt-5">
                {socialLinks.map((s, i) => (
                  <a
                    key={i}
                    href={s.href}
                    target={s.href.startsWith('http') ? '_blank' : undefined}
                    rel="noopener noreferrer nofollow"
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
                  >
                    <i className={`${s.icon} text-gray-400`}></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">
              <Link to="/solutions" className="hover:text-white transition-colors">方案导航</Link>
            </h4>
            <ul className="space-y-2 text-sm">
              {solutionLinks.length > 0 ? (
                solutionLinks.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => {
                        navigate(`/solutions/${item.id}`);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="hover:text-white transition-colors cursor-pointer text-left"
                    >
                      {item.title}
                    </button>
                  </li>
                ))
              ) : (
                <li className="text-gray-500">暂无方案数据</li>
              )}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">
              <Link to="/tools" className="hover:text-white transition-colors">工具下载</Link>
            </h4>
            <ul className="space-y-2 text-sm">
              {toolLinks.length > 0 ? (
                toolLinks.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => {
                        navigate(`/tools/${item.id}`);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="hover:text-white transition-colors cursor-pointer text-left"
                    >
                      {item.name}
                    </button>
                  </li>
                ))
              ) : (
                <li className="text-gray-500">暂无工具数据</li>
              )}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">关于我们</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <button
                  onClick={() => {
                    navigate('/about');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="hover:text-white transition-colors cursor-pointer text-left whitespace-nowrap"
                >
                  关于万象杂货铺
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    navigate('/member');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="hover:text-white transition-colors cursor-pointer text-left whitespace-nowrap"
                >
                  会员中心
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    navigate('/about');
                    setTimeout(() => {
                      const el = document.getElementById('contact');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }, 300);
                  }}
                  className="hover:text-white transition-colors cursor-pointer text-left whitespace-nowrap"
                >
                  联系我们
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <p>{copyright || `© ${new Date().getFullYear()} ${siteName}`}</p>
          <div className="flex items-center gap-4">
            {icp && <p>{icp}</p>}
            <p>内容仅供参考，使用开源软件请遵守对应许可证。</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
